/**
 * Stable error codes the API returns beside the message on a 401/400.
 * Branch on these, never on the message text — the copy is free to change.
 */
export const API_ERROR_CODES = {
  /** Well-formed, correctly signed, but past its lifetime — an aged-out link. */
  TOKEN_EXPIRED: "token_expired",
  /** Any other unusable credential: bad signature, malformed, already used. */
  TOKEN_INVALID: "token_invalid",
  /** Invited but never onboarded, so there is no password to reset yet. */
  ACCOUNT_NOT_ACTIVATED: "account_not_activated",
} as const;

type ErrorWithResponse = { response?: { status?: unknown; data?: { code?: unknown } } };

/**
 * Reads the API's machine-readable error code off a failed request.
 *
 * Reads the shape rather than calling axios's `isAxiosError`, unlike the equivalent in
 * Merchant-Dashboard: this app runs jest through CRA, which does not transform axios's ESM
 * build, so importing it here would cost a build-config change to gain nothing. Anything
 * without a string `response.data.code` returns null and the caller keeps its fallback.
 */
export function getApiErrorCode(error: unknown): string | null {
  if (typeof error !== "object" || error === null) return null;
  const code = (error as ErrorWithResponse).response?.data?.code;
  return typeof code === "string" ? code : null;
}

/** The HTTP status of a failed request, or null when there was no response (e.g. a dropped connection). */
export function getApiStatus(error: unknown): number | null {
  if (typeof error !== "object" || error === null) return null;
  const status = (error as ErrorWithResponse).response?.status;
  return typeof status === "number" ? status : null;
}

/** The link was ours and correctly signed, but its lifetime has passed. */
export function isTokenExpiredError(error: unknown): boolean {
  return getApiErrorCode(error) === API_ERROR_CODES.TOKEN_EXPIRED;
}

/** The link cannot be used at all — altered in transit, or already spent. */
export function isTokenInvalidError(error: unknown): boolean {
  return getApiErrorCode(error) === API_ERROR_CODES.TOKEN_INVALID;
}

/**
 * The account has never been onboarded, so a reset can never succeed for it.
 * These users need their activation link, not another reset email.
 */
export function isAccountNotActivatedError(error: unknown): boolean {
  return getApiErrorCode(error) === API_ERROR_CODES.ACCOUNT_NOT_ACTIVATED;
}
